import classnames from 'classnames';
import * as React from 'react';
import { translate } from '../../../../common/utilities/localisation/translationUtilities';

/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ProductPrice } from '@msdyn365-commerce/retail-proxy';

export interface IPriceComponentProps extends IComponentProps<{ price: ProductPrice }> {
    className?: string;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
}
export interface IPriceComponent extends IComponent<IPriceComponentProps> {
}

const PriceComponentActions = {
};

const Price: React.FC<IPriceComponentProps> = (props: IPriceComponentProps) => {
    // CustomerContextualPrice could be 0
    if (props.data.price.CustomerContextualPrice === undefined) {
        return null;
    }
    return (
        <span className={classnames('msc-price', props.className)}>
            {showStrikethroughPricing(props) ? renderCurrentPriceWithOriginalPrice(props) : renderCurrentPrice(props)}
        </span>
    );
};

const showStrikethroughPricing = (props: IPriceComponentProps) => {
    const originalPrice = getOriginalPrice(props);
    if (originalPrice && props.data.price.CustomerContextualPrice) {
        return originalPrice > props.data.price.CustomerContextualPrice;
    }

    return false;
};

const getOriginalPrice = (props: IPriceComponentProps) => {
    return props.data.price.TradeAgreementPrice || props.data.price.BasePrice || 0;
};

const renderCurrentPrice = (props: IPriceComponentProps): JSX.Element | undefined => {
    const initialPrice = formatCurrency(props, props.data.price.CustomerContextualPrice);
    return <span className="msc-price__actual" itemProp="price">{initialPrice}</span>;
};

const formatCurrency = (props: IPriceComponentProps, price: number | undefined): string | undefined => {
    if (price === undefined) {
        return undefined;
    }

    if (price === 0 && props.freePriceText) {
        return props.freePriceText;
    }

    return props.context.cultureFormatter.formatCurrency(price);
};

const renderCurrentPriceWithOriginalPrice = (props: IPriceComponentProps): JSX.Element | null => {
    const locale = props.context.request.locale;
    const originalPrice = getOriginalPrice(props);
    const initialPrice = formatCurrency(props, originalPrice);
    let discountAmount = formatCurrency(props, props.data.price.DiscountAmount);
    let discountPrice = 0;
    if (props.data.price.CustomerContextualPrice && initialPrice !== undefined) {
        discountPrice = originalPrice - props.data.price.CustomerContextualPrice
        discountAmount = formatCurrency(props, originalPrice - props.data.price.CustomerContextualPrice);
    }

    const saveAmtFmt = translate(locale, 'price-component.save-amt', 'Save {0}');


    return (
        <>
            <span className="sr-only"> {props.originalPriceText} {initialPrice} {props.currentPriceText} {renderCurrentPrice(props)}</span>
            {discountPrice > 0 ? <span className="msc-price__savings">{saveAmtFmt.replace('{0}', discountAmount || '')}</span> : null}
            <span className="msc-price__strikethrough" aria-hidden="true">{initialPrice}</span>
            <span aria-hidden="true">{renderCurrentPrice(props)}</span>
        </>
    );
};

// @ts-ignore
export const PriceComponent: React.FunctionComponent<IPriceComponentProps> = msdyn365Commerce.createComponentOverride<IPriceComponent>(
    'Price',
    { component: Price, ...PriceComponentActions }
);

export default PriceComponent;
